import { FC, useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { BBColumnDef, Organization } from "../../../../types"
import OrganizationCell from "./cells/organization"
import MenuCell from "./cells/menu"
import Table from "../../table"
import EditOrganization from "../edit"
import BBDialog from "../../dialog"
import { useToast } from "../../../hooks"
import { useDeleteOrganizationMutation } from "../../../../redux/queries"

interface Props {
  data: Organization[]
  isLoading: boolean
}

const OrganizationsTable: FC<Props> = ({ data, isLoading }) => {
  const navigate = useNavigate()
  const { pushSuccessToast, pushErrorToast } = useToast()

  const [selectedOrg, setSelectedOrg] = useState<Organization>()
  const [orgIdToDelete, setOrgIdToDelete] = useState<string>("")

  const [deleteOrg, { isSuccess, isError, reset }] =
    useDeleteOrganizationMutation()

  const onClick = (id: string) => navigate(`/organizations/${id}`)

  const onDelete = (id: string) => {
    setOrgIdToDelete(id)
  }

  const clearOrgToDelete = () => {
    setOrgIdToDelete("")
    reset()
  }

  const handleDelete = () => {
    if (orgIdToDelete) {
      deleteOrg(orgIdToDelete)
    }
  }

  useEffect(() => {
    if (isSuccess && orgIdToDelete) {
      pushSuccessToast("Organization deleted")
      setOrgIdToDelete("")
      reset()
    }
  }, [isSuccess, orgIdToDelete, pushSuccessToast, reset])

  useEffect(() => {
    if (isError) {
      pushErrorToast("Failed to delete organization")
      setOrgIdToDelete("")
      reset()
    }
  }, [isError, pushErrorToast, reset])

  const resetSelected = () => setSelectedOrg(undefined)

  const columns: BBColumnDef<Organization>[] = [
    {
      id: "organization",
      header: "Organization",
      accessorFn: row => row.name,
      footer: props => props.column.id,
      cell: props => (
        <OrganizationCell info={props.row.original} onClick={onClick} />
      )
    },
    {
      id: "edit",
      header: "",
      footer: props => props.column.id,
      cell: props => (
        <MenuCell
          info={props.row.original}
          onEdit={setSelectedOrg}
          onDelete={onDelete}
        />
      )
    }
  ]
  return (
    <div className="pt-4">
      <Table
        data={data}
        columns={columns}
        viewType="table"
        dropdownFilterColumnId="organization"
        autoResetPageIndex={false}
        searchPlaceholder="Search organizations"
        shrinkFilterSort
        isLoading={isLoading}
      />
      <EditOrganization
        open={!!selectedOrg}
        data={selectedOrg}
        onClose={resetSelected}
        isInSidePanel
      />
      <BBDialog
        open={!!orgIdToDelete}
        onClose={clearOrgToDelete}
        title="Delete organization"
        body="By doing this all the information stored in the system about this organization will be deleted. Are you sure?"
        okLabel="Yes, Delete"
        cancelLabel="Cancel"
        onOk={handleDelete}
        onCancel={clearOrgToDelete}
      />
    </div>
  )
}

export default OrganizationsTable
